import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  findScrollPort,
  isDocumentScroller,
  measureTopCover,
  portVisibleTop,
  scrollPaddingTop,
} from '@/hen-ui/composites/StickyBar/reserve-scroll-padding';
import {
  findScrollPoint,
  resolveObserveTarget,
} from '@/hen-ui/composites/StickyBar/scroll-point';

const STICKY_BAR_SELECTOR = `[data-hen-id="${COMPONENT_IDS.StickyBar}"]`;

type Moved = { port: HTMLElement; by: number }[];

function prefersReducedMotion(view: Window): boolean {
  return (
    typeof view.matchMedia === 'function' &&
    view.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function toPx(value: string | undefined): number {
  const px = Number.parseFloat(value ?? '');
  return Number.isFinite(px) ? px : 0;
}

function isTopDocked(bar: HTMLElement): boolean {
  if (bar.getAttribute('data-hen-sticky-edge') !== 'top') return false;
  const view = bar.ownerDocument.defaultView;
  if (!view) return false;
  const { position } = view.getComputedStyle(bar);
  return position === 'fixed' || position === 'sticky';
}

/** Scrollable ancestors of `target`, innermost first, ending with the document. */
function scrollChain(target: HTMLElement): HTMLElement[] {
  const doc = target.ownerDocument;
  const chain: HTMLElement[] = [];
  let port = findScrollPort(target);
  while (!isDocumentScroller(port, doc)) {
    chain.push(port);
    port = findScrollPort(port);
  }
  chain.push(port);
  return chain;
}

/** How far the ports scrolled so far have moved `element` up the screen. */
function shiftOf(element: Element, moved: Moved): number {
  const doc = element.ownerDocument;
  return moved.reduce(
    (sum, { port, by }) =>
      isDocumentScroller(port, doc) || port.contains(element) ? sum + by : sum,
    0,
  );
}

/**
 * Whether `bar` will be on screen once `port` scrolls to `landing`. A bar
 * waiting for a scroll point is judged at the landing without its own
 * clearance: where clearing it would bring the marker back on screen, the
 * target is left with a gap above it rather than under the bar.
 */
function shownAfterJump(
  bar: HTMLElement,
  port: HTMLElement,
  landing: number,
  moved: Moved,
): boolean {
  const doc = bar.ownerDocument;
  const after = bar.getAttribute('data-hen-sticky-after');
  const marker = after ? findScrollPoint(after, doc) : null;
  if (!marker) return bar.getAttribute('data-hen-sticky-revealed') === 'true';
  // `display: contents` hosts have no box, so measure the block they wrap.
  const block = resolveObserveTarget(marker);
  const scrolls = isDocumentScroller(port, doc) || port.contains(block);
  const bottom =
    block.getBoundingClientRect().bottom -
    shiftOf(block, moved) -
    (scrolls ? landing - port.scrollTop : 0);
  return bottom <= 1;
}

function stickyClearance(
  bars: HTMLElement[],
  port: HTMLElement,
  landing: number,
  moved: Moved,
): number {
  let clearance = 0;
  for (const bar of bars) {
    if (!isTopDocked(bar)) continue;
    // Clipped to this scrollport, so a bar above a nested scroller adds nothing.
    const cover = measureTopCover(bar, port);
    if (cover <= clearance) continue;
    if (shownAfterJump(bar, port, landing, moved)) clearance = cover;
  }
  return clearance;
}

/**
 * Scrolls `target` to the top of every scrollport it sits in, as
 * `scrollIntoView` does, keeping its `scroll-margin-top` and the port's
 * `scroll-padding-top`, and below any top bar that will be docked once the
 * jump lands.
 */
export function scrollAnchorIntoView(target: HTMLElement): void {
  const doc = target.ownerDocument;
  const view = doc.defaultView;
  if (!view) return;

  const bars = Array.from(
    doc.querySelectorAll<HTMLElement>(STICKY_BAR_SELECTOR),
  );
  // StickyBar's own reservations are replaced by the landing prediction.
  const barReservations = new Set<unknown>(bars);
  const margin = toPx(view.getComputedStyle(target).scrollMarginTop);
  const behavior = prefersReducedMotion(view) ? 'auto' : 'smooth';
  const moved: Moved = [];

  for (const port of scrollChain(target)) {
    const offset =
      target.getBoundingClientRect().top -
      shiftOf(target, moved) +
      port.scrollTop -
      portVisibleTop(port, doc);
    const padding = scrollPaddingTop(port, barReservations);
    const landing = offset - margin - padding;
    const clearance = stickyClearance(bars, port, landing, moved);
    const top = Math.max(0, offset - margin - Math.max(padding, clearance));

    if (isDocumentScroller(port, doc)) {
      view.scrollTo({ top, behavior });
    } else {
      port.scrollTo({ top, behavior });
    }
    const limit = Math.max(0, port.scrollHeight - port.clientHeight);
    moved.push({ port, by: Math.min(top, limit) - port.scrollTop });
  }
}
